import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { isPlatformBrowser } from '@angular/common';
import { AlertService, AuthenticationService, GMailService, ScanGMailRejectionReason, SuccessfulStartIntegrationResponse } from 'src/app/_services';
import { environment } from 'src/environments/environment';
import { Subscription } from 'rxjs';

@Component({
  selector: 'scan-gmail-button',
  templateUrl: './scan-gmail-button.component.html',
  styleUrls: ['./scan-gmail-button.component.css']
})
export class ScanGMailButtonComponent implements OnInit, OnDestroy {
  
  private _scanning: boolean = false;
  
  @Output() onScanned: EventEmitter<string> = new EventEmitter();
  @Output() onScanStarted: EventEmitter<string> = new EventEmitter();
  @Input() label = $localize `Scan Gmail`;
  @Input() type = "mat-button";
  @Input() color = "basic";
  
  private queryParamsSubscription: Subscription;
  private polling: boolean = false;
  private pollingTimer: any;
  
  constructor(
    private gmailService: GMailService,
    private router: Router,
    private route: ActivatedRoute,
    private alertService: AlertService,
    private authenticationService: AuthenticationService,
    private location: Location,
    @Inject(PLATFORM_ID) private platformId: any
  ) {

    this.queryParamsSubscription = this.route.queryParams.subscribe(params => {
      
      if (params["confirmGMailIntegration"] !== undefined) {
        this.confirmIntegration(params["code"], params["state"]);
      }
      
      if (params["startGMailScan"] !== undefined) {
        this.scanGMail();
      }
    });
  }
  
  ngOnInit(): void {
  }
  
  ngOnDestroy() {
    if (this.queryParamsSubscription) {
      this.queryParamsSubscription.unsubscribe();
    }
  }
  
  private calculateCallbackURL(): string {
    const url = environment['loginCallbackBaseURL'] + this.location.path().split('?')[0] + "?confirmGMailIntegration";
    
    console.log('Callback URL would be ' + url);
    
    return url;
  }
  
  private calculateLoginCallbackURL(): string {
    return environment['loginCallbackBaseURL'] + this.location.path().split('?')[0] + "?startGMailScan"
  }
  
  public scanGMail(): void {

    this._scanning = true;
    
    if (!this.authenticationService.isAuthenticated()) {
      console.log("Need to login before scanning GMail");
      this.authenticationService.login(this.calculateLoginCallbackURL());
    } else {
      this.gmailService.scanGMail().subscribe((response) => {
        if (response instanceof SuccessfulStartIntegrationResponse) {
          console.log("Scan started");
          this.schedulePoll();
          this.onScanStarted.emit();
        } else if (response.reason === ScanGMailRejectionReason.NO_AUTHORIZATION) {
          this.gmailService.startIntegration(this.calculateCallbackURL()).subscribe(uri => {
            if (isPlatformBrowser(this.platformId)) {
              window.location.href = uri;
            } else {
              console.log("Not available on server");
            }
          }, () => {
            this._scanning = false;
          });
        }
      }, (error: any) => {
        this._scanning = false;
      });
    }
    
    this.clearQueryParameters();
  }
  
  private async schedulePoll(): Promise<void> {
    
    if (this.polling) {
      console.log("Already polling");
      return;
    }
    
    this.pollingTimer = setTimeout(() => { this.poll() }, 5000);
    this.polling = true;
  }
  
  async poll(): Promise<void> {

    console.log("Polling for scan progress");
    
    if (await this.hasOngoingScan()) {
      console.log("Scheduling another poll");
      this.pollingTimer = setTimeout(() => { this.poll() }, 5000);      
    } else {
      console.log("No more need to poll, scan completed");
      this.completeScan();
      this.polling = false;
    }
  }
  
  private async hasOngoingScan(): Promise<boolean> {
    return (await this.gmailService.listOngoingScans()).length > 0;
  }
  
  private completeScan() {
    this.alertService.showSuccess($localize `Gmail scanned successfuly`);
    this._scanning = false;
    this.onScanned.emit();
  }
  
  private clearQueryParameters() {
    this.router.navigate([], {
      queryParams: {
        'code': null,
        'state': null,
        'scope': null,
        'confirmGMailIntegration': null,
        'startGMailScan': null
      },
      queryParamsHandling: 'merge'
    });
  }
  
  private confirmIntegration(code: string, state: string) {
    
    console.log("Confirming GMail integration");
    
    this.gmailService.createIntegration(code, state, this.calculateCallbackURL()).subscribe((integrationId) => {
      console.log("Integration confirmed");
      this.clearQueryParameters();
      this.scanGMail()
    });
  }
  
  public get scanning() {
    return this._scanning;
  }
}
